import React, { useState, useEffect } from 'react'
import { 
  FaUsers, 
  FaSearch, 
  FaArrowRight, 
  FaUserTie,
  FaCrown,
  FaMapMarkerAlt,
  FaCalendarAlt
} from 'react-icons/fa'
import { useAuth } from '../../context/Auth'
import Loading from '../../components/Loading/Loading';
import { toast } from 'react-toastify';
import { api_url } from '../../data';

const GroupSelection = () => {
    const {requestWithAuth} = useAuth();
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)

  const fetchGroups = async () => {
    try {
      
      const response = await requestWithAuth(`/group-leader/groups/assigned-groups/?search=${search}`, 'GET');
      setGroups(response.data);
    } catch (error) {
      toast.error("Failed to load group details");
    } finally {
      setLoadingGroups(false);
    }
  };

  // Mock data - replace with actual API call
  useEffect(() => {
    fetchGroups()
  }, [search])

  

  const handleProceedToGroup = () => {
    if (!selectedGroup) return
    
    setLoading(true)
    
    // Simulate API call to set active group and redirect
    setTimeout(() => {
      window.location.href = `/groups/dashboard`
      localStorage.setItem('selectedGroupId', selectedGroup.group_id);
      setLoading(false)
    }, 1000)
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'President':
        return <FaCrown className="text-yellow-500" />
      case 'Vice President':
      case 'Co-Leader':
        return <FaUserTie className="text-blue-500" />
      default:
        return <FaUsers className="text-gray-500" />
    }
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'President':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'Vice President':
      case 'Co-Leader':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  if(loadingGroups) return <Loading />

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome! 👋
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Select a group to manage and get started
          </p>
          <p className="text-gray-500">
            You have leadership access to {groups?.length} groups
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
            />
          </div>
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {groups?.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <FaUsers className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
              <p className="text-gray-500">
                {search ? 'Try adjusting your search terms' : 'No groups available to manage'}
              </p>
            </div>
          ) : (
            groups?.map((group) => (
              <div
                key={group.group_id}
                onClick={() => setSelectedGroup(group)}
                className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:-translate-y-1 ${
                  selectedGroup?.group_id === group.group_id 
                    ? 'ring-2 ring-blue-500 shadow-xl' 
                    : 'hover:ring-1 hover:ring-gray-200'
                }`}
              >
                {/* Group Logo */}
                <div className="p-6 pb-4">
                  <div className="flex flex-col items-center">
                    <div className="relative mb-4">
                      <img
                        src={`${api_url}${group?.logo}`}
                        alt={group?.name}
                        className="w-20 h-20 rounded-full shadow-md"
                        
                      />
                      {selectedGroup?.group_id === group.group_id && (
                        <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                          <FaCheck className="text-sm" />
                        </div>
                      )}
                    </div>
                    
                    {/* Group Name */}
                    <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                      {group?.name}
                    </h3>
                    
                    {/* Role Badge */}
                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(group.role)} mb-3`}>
                      {getRoleIcon(group?.position)}
                      {group?.position}
                    </div>
                  </div>
                </div>


                {/* Selection Indicator */}
                {selectedGroup?.group_id === group.group_id && (
                  <div className="bg-blue-50 border-t border-blue-100 px-6 py-3 rounded-b-xl">
                    <p className="text-center text-sm text-blue-700 font-medium">
                      ✓ Selected - Ready to proceed
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Action Button */}
        {selectedGroup && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-white rounded-xl shadow-lg p-4 mx-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <img
                    src={`${api_url}${selectedGroup?.logo}`}
                    alt={selectedGroup?.name}
                    className="w-12 h-12 rounded-full"
                    
                  />
                  <div>
                    <p className="font-medium text-gray-900">{selectedGroup?.name}</p>
                    <p className="text-sm text-gray-500">{selectedGroup?.member_count} members</p>
                  </div>
                </div>
                
                <button
                  onClick={handleProceedToGroup}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      Manage Group <FaArrowRight />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        
      </div>
    </div>
  )
}

const FaCheck = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
)

export default GroupSelection